/**
 * SOLID Review
 * S — Strong. sole job: implement IAdapterApolloGame's round-lifecycle operations against the real
 *     backend via Apollo's imperative client API. Strong for the methods it currently has
 *     (startRound/consumeMatchCredit/getActiveRound/subscribeToRoundStarted — Phase 2 Group B,
 *     plan/plan_frontend_makeover.md, LobbyPanel.tsx's real operations; announceClaim/
 *     believeClaim/liftCup/subscribeToClaimAnnounced/subscribeToCupLifted — Phase 2 Group C,
 *     ControlPanel.tsx's real operations; rollDice/peekDice/subscribeToDiceRolled — Phase 2
 *     Group D, GameSceneCore.tsx's real operations, correcting a previously-fabricated
 *     gameId/turnId/IEntityDiceRoll signature to the real roundId/playerId/
 *     {die1,die2,score,isMeyer,isDouble} shape). makeClaim/makeDecision/getGameSession were
 *     deleted as housekeeping (plan/plan_frontend_makeover.md) — fabricated/idealized
 *     placeholders with zero known callers anywhere in the tree, confirmed after Phase 2
 *     completed.
 * O — Good. A new round-lifecycle operation is a new method here plus a new IAdapterApolloGame signature —
 *     mechanical, no existing method touched.
 * L — N/A — no inheritance hierarchy; implements IAdapterApolloGame directly.
 * I — Good. Implements the same interface GameSceneCore.tsx/ControlPanel.tsx's real operations need — no
 *     longer silently narrower than the interface promises now that its three fabricated placeholders
 *     (makeClaim/makeDecision/getGameSession) are gone.
 * D — Strong. Takes ApolloClient as a constructor-injected dependency (same pattern as
 *     ApolloRoomAdapter.ts) rather than importing a singleton — testable without a real network.
 *     subscribeToRoundStarted deliberately relays the raw server event unfiltered — the room-scoping
 *     defense-in-depth guard stays in the caller (LobbyPanel.tsx), matching ApolloRoomAdapter.ts's own
 *     subscribe-method precedent.
 */
import { gql } from '@apollo/client';
import type { Result } from '../../shared/Result';
import type { RoomId, PlayerId } from '../../application/ports/ClientVocabulary';
import type { IEntityRoundInfo, IEntityActiveRound, IPayloadClaim, IEntityChallenge, IEntityRoll, IEntityDicePair } from '../../application/ports/apolloShapes';
import { ok, fail } from '../../shared/Result';
import type { IAdapterApolloGame, IEntityActiveRoundRestore } from '../../application/ports/IAdapterApolloGame';
import type { IPortGraphQLDomainClient } from '../contract/ports';

// ---------------------------------------------------------------------------
// GraphQL operations (Phase 2 Group B). Moved here from LobbyPanel.tsx's inline definitions.
// ---------------------------------------------------------------------------

const START_ROUND_MUTATION = gql`
  mutation ApolloGameAdapter_StartRound($roomId: ID!, $rollerId: ID!) {
    startRound(roomId: $roomId, rollerId: $rollerId) {
      roundId roomId rollerId status minimumScore
    }
  }
`;

const GET_ACTIVE_ROUND_QUERY = gql`
  query ApolloGameAdapter_GetActiveRound($roomId: ID!) {
    getActiveRound(roomId: $roomId) {
      roundId rollerId diceRolled
      pendingClaim { claimedScore label challengerId }
      minimumScore
      playerLives { playerId lives }
    }
  }
`;

/** The restore read's shape, taken from the application port so the two cannot drift. */
type TRestoreActiveRound = IEntityActiveRoundRestore | null;

const GET_ACTIVE_ROUND_FOR_RESTORE_QUERY = gql`
  query ApolloGameAdapter_GetActiveRoundForRestore($roomId: ID!) {
    getActiveRound(roomId: $roomId) {
      roundId
      rollerId
      diceRolled
      pendingClaim { claimedScore label challengerId wasBlind }
      minimumScore
      playerLives { playerId lives }
      revealResult {
        losingPlayerId wasBluff claimedScore trueScore die1 die2 newRollerId
      }
    }
  }
`;

const ROLL_DICE_MUTATION = gql`
  mutation ApolloGameAdapter_RollDice($roundId: String!, $playerId: ID!) {
    rollDice(roundId: $roundId, playerId: $playerId) { roundId }
  }
`;

const PEEK_DICE_MUTATION = gql`
  mutation ApolloGameAdapter_PeekDice($roundId: String!, $playerId: ID!) {
    peekDice(roundId: $roundId, playerId: $playerId) {
      die1 die2 score isMeyer isDouble
    }
  }
`;

const DICE_ROLLED_SUB = gql`
  subscription ApolloGameAdapter_DiceRolled($roundId: String!, $roomId: ID) {
    diceRolled(roundId: $roundId, roomId: $roomId) { roundId }
  }
`;

const ROUND_STARTED_SUB = gql`
  subscription ApolloGameAdapter_RoundStarted($roomId: ID!) {
    roundStarted(roomId: $roomId) {
      roundId roomId rollerId status minimumScore
    }
  }
`;

const ANNOUNCE_CLAIM_MUTATION = gql`
  mutation ApolloGameAdapter_AnnounceClaim($roundId: String!, $playerId: ID!, $claimedScore: Int!) {
    announceClaim(roundId: $roundId, playerId: $playerId, claimedScore: $claimedScore) {
      roundId claimerId claimedScore label wasBlind challengerId
    }
  }
`;

/**
 * The blind pass. A separate mutation rather than announceClaim + a flag, so that a client
 * which believes it is passing blind — after a peek it has lost track of — is told so, instead
 * of having its claim silently downgraded to a peeked one (§28.62).
 */
const ANNOUNCE_BLIND_MUTATION = gql`
  mutation ApolloGameAdapter_AnnounceBlind($roundId: String!, $playerId: ID!, $claimedScore: Int!) {
    announceBlind(roundId: $roundId, playerId: $playerId, claimedScore: $claimedScore) {
      roundId claimerId claimedScore label wasBlind challengerId
    }
  }
`;

const BELIEVE_CLAIM_MUTATION = gql`
  mutation ApolloGameAdapter_BelieveClaim($roundId: String!, $playerId: ID!, $roomId: String!, $minimumScore: Int!) {
    believeClaim(roundId: $roundId, playerId: $playerId, roomId: $roomId, minimumScore: $minimumScore) {
      roundId roomId rollerId status minimumScore
    }
  }
`;

const LIFT_CUP_MUTATION = gql`
  mutation ApolloGameAdapter_LiftCup($roundId: String!, $playerId: ID!) {
    liftCup(roundId: $roundId, playerId: $playerId) {
      roundId roomId trueScore claimedScore losingPlayerId wasBluff newRollerId die1 die2 endsGame loserLivesLeft
    }
  }
`;

const CLAIM_ANNOUNCED_SUB = gql`
  subscription ApolloGameAdapter_ClaimAnnounced($roundId: String!, $roomId: ID) {
    claimAnnounced(roundId: $roundId, roomId: $roomId) {
      roundId claimerId claimedScore label wasBlind challengerId
    }
  }
`;

const CUP_LIFTED_SUB = gql`
  subscription ApolloGameAdapter_CupLifted($roundId: String!, $roomId: ID) {
    cupLifted(roundId: $roundId, roomId: $roomId) {
      roundId roomId trueScore claimedScore losingPlayerId wasBluff newRollerId die1 die2 endsGame loserLivesLeft
    }
  }
`;

export class ApolloGameAdapter implements IAdapterApolloGame {
  // The executor arrives from this domain's socket on ComponentFrontendInfrastructure, not as
  // the raw ApolloClient. That socket is the single path from "this app can reach the domain"
  // to "this adapter executes" — disconnect it and this adapter degrades to the Null client
  // instead of quietly using a module singleton (§28.72).
  constructor(private readonly client: IPortGraphQLDomainClient) {}

  async rollDice(roundId: string, playerId: PlayerId): Promise<Result<IEntityRoll>> {
    try {
      const { data, error } = await this.client.mutate<{ rollDice: IEntityRoll | null }>({
        mutation: ROLL_DICE_MUTATION,
        variables: { roundId, playerId },
      });
      if (error) {
        return fail(new Error(`ApolloGameAdapter.rollDice returned a GraphQL error: ${error.message}`));
      }
      if (!data?.rollDice) {
        return fail(new Error('ApolloGameAdapter.rollDice returned no data'));
      }
      return ok(data.rollDice);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error rolling dice';
      return fail(new Error(`ApolloGameAdapter.rollDice failed: ${message}`));
    }
  }

  async peekDice(roundId: string, playerId: PlayerId): Promise<Result<IEntityDicePair>> {
    try {
      const { data, error } = await this.client.mutate<{ peekDice: IEntityDicePair | null }>({
        mutation: PEEK_DICE_MUTATION,
        variables: { roundId, playerId },
      });
      if (error) {
        return fail(new Error(`ApolloGameAdapter.peekDice returned a GraphQL error: ${error.message}`));
      }
      if (!data?.peekDice) {
        return fail(new Error('ApolloGameAdapter.peekDice returned no data'));
      }
      return ok(data.peekDice);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error peeking dice';
      return fail(new Error(`ApolloGameAdapter.peekDice failed: ${message}`));
    }
  }

  async startRound(roomId: RoomId, rollerId: PlayerId): Promise<Result<IEntityRoundInfo>> {
    try {
      const { data, error } = await this.client.mutate<{ startRound: IEntityRoundInfo | null }>({
        mutation: START_ROUND_MUTATION,
        variables: { roomId, rollerId },
      });
      if (error) {
        return fail(new Error(`ApolloGameAdapter.startRound returned a GraphQL error: ${error.message}`));
      }
      if (!data?.startRound) {
        return fail(new Error('ApolloGameAdapter.startRound returned no data'));
      }
      return ok(data.startRound);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error starting round';
      return fail(new Error(`ApolloGameAdapter.startRound failed: ${message}`));
    }
  }

  /**
   * MOVED FROM framework/graphql/restoreOperations.ts (2026-08-30), where it was driven by a
   * useLazyQuery in AppRoot. `network-only` is preserved: a restore that reads a cached round
   * would put the player back on a screen the server has already moved past.
   */
  async getActiveRoundForRestore(roomId: RoomId): Promise<Result<TRestoreActiveRound>> {
    try {
      const { data, error } = await this.client.query<{ getActiveRound: TRestoreActiveRound }>({
        query: GET_ACTIVE_ROUND_FOR_RESTORE_QUERY,
        variables: { roomId },
        fetchPolicy: 'network-only',
      });
      if (error) {
        return fail(new Error(`ApolloGameAdapter.getActiveRoundForRestore returned a GraphQL error: ${error.message}`));
      }
      return ok(data?.getActiveRound ?? null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error reading the active round';
      return fail(new Error(`ApolloGameAdapter.getActiveRoundForRestore failed: ${message}`));
    }
  }

  async getActiveRound(roomId: RoomId): Promise<Result<IEntityActiveRound | null>> {
    try {
      const { data, error } = await this.client.query<{ getActiveRound: IEntityActiveRound | null }>({
        query: GET_ACTIVE_ROUND_QUERY,
        variables: { roomId },
        fetchPolicy: 'network-only',
      });
      if (error) {
        return fail(new Error(`ApolloGameAdapter.getActiveRound returned a GraphQL error: ${error.message}`));
      }
      return ok(data?.getActiveRound ?? null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error fetching active round';
      return fail(new Error(`ApolloGameAdapter.getActiveRound failed: ${message}`));
    }
  }

  subscribeToRoundStarted(roomId: RoomId, onEvent: (ev: IEntityRoundInfo) => void): () => void {
    const sub = this.client.subscribe<{ roundStarted: IEntityRoundInfo }>({ query: ROUND_STARTED_SUB, variables: { roomId } }).subscribe({
      next: ({ data }) => {
        if (data?.roundStarted) onEvent(data.roundStarted);
      },
      error: (err: unknown) => console.warn('[ApolloGameAdapter] roundStarted subscription error:', err),
    });
    return () => sub.unsubscribe();
  }

  async announceClaim(roundId: string, playerId: PlayerId, claimedScore: number): Promise<Result<IPayloadClaim>> {
    try {
      const { data, error } = await this.client.mutate<{ announceClaim: IPayloadClaim | null }>({
        mutation: ANNOUNCE_CLAIM_MUTATION,
        variables: { roundId, playerId, claimedScore },
      });
      if (error) {
        return fail(new Error(`ApolloGameAdapter.announceClaim returned a GraphQL error: ${error.message}`));
      }
      if (!data?.announceClaim) {
        return fail(new Error('ApolloGameAdapter.announceClaim returned no data'));
      }
      return ok(data.announceClaim);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error announcing claim';
      return fail(new Error(`ApolloGameAdapter.announceClaim failed: ${message}`));
    }
  }

  async announceBlind(roundId: string, playerId: PlayerId, claimedScore: number): Promise<Result<IPayloadClaim>> {
    try {
      const { data, error } = await this.client.mutate<{ announceBlind: IPayloadClaim | null }>({
        mutation: ANNOUNCE_BLIND_MUTATION,
        variables: { roundId, playerId, claimedScore },
      });
      if (error) {
        return fail(new Error(`ApolloGameAdapter.announceBlind returned a GraphQL error: ${error.message}`));
      }
      if (!data?.announceBlind) {
        return fail(new Error('ApolloGameAdapter.announceBlind returned no data'));
      }
      return ok(data.announceBlind);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error announcing blind claim';
      return fail(new Error(`ApolloGameAdapter.announceBlind failed: ${message}`));
    }
  }

  async believeClaim(roundId: string, playerId: PlayerId, roomId: RoomId, minimumScore: number): Promise<Result<IEntityRoundInfo>> {
    try {
      const { data, error } = await this.client.mutate<{ believeClaim: IEntityRoundInfo | null }>({
        mutation: BELIEVE_CLAIM_MUTATION,
        variables: { roundId, playerId, roomId, minimumScore },
      });
      if (error) {
        return fail(new Error(`ApolloGameAdapter.believeClaim returned a GraphQL error: ${error.message}`));
      }
      if (!data?.believeClaim) {
        return fail(new Error('ApolloGameAdapter.believeClaim returned no data'));
      }
      return ok(data.believeClaim);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error believing claim';
      return fail(new Error(`ApolloGameAdapter.believeClaim failed: ${message}`));
    }
  }

  async liftCup(roundId: string, playerId: PlayerId): Promise<Result<IEntityChallenge>> {
    try {
      const { data, error } = await this.client.mutate<{ liftCup: IEntityChallenge | null }>({
        mutation: LIFT_CUP_MUTATION,
        variables: { roundId, playerId },
      });
      if (error) {
        return fail(new Error(`ApolloGameAdapter.liftCup returned a GraphQL error: ${error.message}`));
      }
      if (!data?.liftCup) {
        return fail(new Error('ApolloGameAdapter.liftCup returned no data'));
      }
      return ok(data.liftCup);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error lifting cup';
      return fail(new Error(`ApolloGameAdapter.liftCup failed: ${message}`));
    }
  }

  subscribeToClaimAnnounced(roundId: string, roomId: RoomId | null, onEvent: (ev: IPayloadClaim) => void): () => void {
    const sub = this.client.subscribe<{ claimAnnounced: IPayloadClaim }>({ query: CLAIM_ANNOUNCED_SUB, variables: { roundId, roomId } }).subscribe({
      next: ({ data }) => {
        if (data?.claimAnnounced) onEvent(data.claimAnnounced);
      },
      error: (err: unknown) => console.warn('[ApolloGameAdapter] claimAnnounced subscription error:', err),
    });
    return () => sub.unsubscribe();
  }

  subscribeToCupLifted(roundId: string, roomId: RoomId | null, onEvent: (ev: IEntityChallenge) => void): () => void {
    const sub = this.client.subscribe<{ cupLifted: IEntityChallenge }>({ query: CUP_LIFTED_SUB, variables: { roundId, roomId } }).subscribe({
      next: ({ data }) => {
        if (data?.cupLifted) onEvent(data.cupLifted);
      },
      error: (err: unknown) => console.warn('[ApolloGameAdapter] cupLifted subscription error:', err),
    });
    return () => sub.unsubscribe();
  }

  subscribeToDiceRolled(roundId: string, roomId: RoomId | null, onEvent: (ev: IEntityRoll) => void): () => void {
    const sub = this.client.subscribe<{ diceRolled: IEntityRoll }>({ query: DICE_ROLLED_SUB, variables: { roundId, roomId } }).subscribe({
      next: ({ data }) => {
        if (data?.diceRolled) onEvent(data.diceRolled);
      },
      error: (err: unknown) => console.warn('[ApolloGameAdapter] diceRolled subscription error:', err),
    });
    return () => sub.unsubscribe();
  }
}
